import React, { FC, useMemo, useState } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'
import { useShareTask } from 'modules/tasks/hooks/useShareTask'
import {
  SharedAccessesData,
  useSharedAccessesForTask,
} from 'modules/tasks/hooks/useSharedAccessesForTask'
import { useUnshareTask } from 'modules/tasks/hooks/useUnshareTask'

interface ShareTaskProviderContextProps {
  loading: boolean
  newSharedAccessesFiltered: () => number[]
  saveChanges: () => Promise<void>
  currentSharedAccesses: SharedAccessesData[]
  addedSharedAccesses: number[]
  accessesToRemove: number[]
  removeAccess: (accessId: number) => void
  addAccess: (accessId: number[]) => void
}

const ShareTaskProviderContext = React.createContext<ShareTaskProviderContextProps | null>(null)

interface ShareTaskProviderProps {
  task: TaskDetail
  children: React.ReactNode
}

const successFlash = () => {
  app.setFlash({
    type: 'correct',
    message: i18n.t('common.saved_successfully'),
  })
}
const wrongFlash = () => {
  app.setFlash({
    type: 'wrong',
    message: i18n.t('errors.generic_saving'),
  })
}

export const ShareTaskProvider: FC<ShareTaskProviderProps> = ({ children, task }) => {
  const { shareTask } = useShareTask()
  const { unshareTask } = useUnshareTask()

  const [addedSharedAccesses, setAddedSharedAccesses] = useState<number[]>([])
  const [accessesToRemove, setAccessesToRemove] = useState<number[]>([])

  const { loading, accesses: sharedAccesses } = useSharedAccessesForTask(task.id)
  const sharedAccessesIds = sharedAccesses.map((i) => i.id)

  const currentSharedAccessesIds = useMemo(() => {
    return sharedAccesses.map((i) => i.id)
  }, [sharedAccesses])

  // Filter the new accesses to share, removing those accesses which are already shared
  const newAccessesToShare = () => {
    const currentAccessesIds = sharedAccesses.map((i) => i.id)

    return addedSharedAccesses.filter((i) => !currentAccessesIds.includes(i))
  }

  const addAccess = (accessIds: number[]) => {
    setAccessesToRemove((prev) => prev.filter((i) => !accessIds.includes(i)))

    setAddedSharedAccesses((prev) => [prev, accessIds].flat())
  }

  const removeAccess = (accessId: number) => {
    if (sharedAccessesIds.includes(accessId)) {
      setAccessesToRemove((prev) => [prev, accessId].flat())
    }

    setAddedSharedAccesses((prev) => prev.filter((i) => i !== accessId))
  }

  const newSharedAccessesFiltered = () => {
    return addedSharedAccesses.filter((item) => !currentSharedAccessesIds.includes(item))
  }

  const share = async () => {
    const accesses = newAccessesToShare()

    if (accesses.length) {
      const result = await shareTask({ taskId: task.id, sharedIds: accesses })

      const handled = handleResult(result, () => {})

      handled.success ? successFlash() : wrongFlash()
    } else {
      successFlash()
    }
  }

  const unshare = async () => {
    const validAccesses = accessesToRemove.filter((i) => !addedSharedAccesses.includes(i))

    if (validAccesses.length > 0) {
      await unshareTask({ taskId: task.id, accessIds: validAccesses })
    }
  }

  const saveChanges = async () => {
    unshare()
    share()
  }

  useEffect(() => {
    if (!loading) setAddedSharedAccesses(sharedAccesses.map((i) => i.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  return (
    <ShareTaskProviderContext.Provider
      value={{
        loading,
        saveChanges,
        addedSharedAccesses,
        accessesToRemove,
        addAccess,
        currentSharedAccesses: sharedAccesses,
        removeAccess,
        newSharedAccessesFiltered,
      }}
    >
      {children}
    </ShareTaskProviderContext.Provider>
  )
}

export const useShareTaskProvider = (): ShareTaskProviderContextProps => {
  const ShareTaskProviderModule = React.useContext(ShareTaskProviderContext)

  if (!ShareTaskProviderModule)
    throw new Error('useShareTaskProvider must be used within an ShareTaskProviderModule')

  return ShareTaskProviderModule
}
